import {
  ApiResponseDTOTransactionDTO,
  OperationsApiService,
  TransactionsApiService,
} from "@zenith/services/transactions";
import { useState, useEffect, useRef } from "react";

type PollIdType = "transaction" | "operation";

export type PollTransactionStatusResponse = ApiResponseDTOTransactionDTO & {
  data?: ApiResponseDTOTransactionDTO["data"] & { sessionId?: string };
};

// Maximum time to poll for transaction status (5 minutes)
const MAX_POLLING_TIME = 5 * 60 * 1000;
// Maximum number of retries before giving up
const MAX_RETRIES = 3;
// Delay between retries (in milliseconds)
const RETRY_DELAY = 2000;

/**
 * Raised when the poll never got a definitive answer from the processor —
 * a network hiccup, the polling cap, or an unrecognised status. This is
 * NOT a decline: the processor's real outcome is still unknown, so callers
 * should resolve to a pending/unknown state rather than treating it as a
 * failed transaction (BOAS-8207 — Processor Authority: only a status the
 * server actually reports can mark a transaction DECLINED/FAILED).
 */
export class TransactionStatusUnknownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TransactionStatusUnknownError";
  }
}

export const usePollTransactionStatus = (
  onSuccess: (data: PollTransactionStatusResponse) => void,
  onError: (error: Error) => void,
  interval = 3000
) => {
  const [isLoading, setIsLoading] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const transactionIdRef = useRef<string>("");
  const idTypeRef = useRef<PollIdType>("transaction");
  const isMountedRef = useRef(true);
  const startTimeRef = useRef<number>(0);
  const retryCountRef = useRef<number>(0);
  const isPollingActiveRef = useRef<boolean>(false);

  const startPolling = (id: string, idType: PollIdType = "transaction") => {
    if (!id) return;

    stopPolling();

    transactionIdRef.current = id;
    idTypeRef.current = idType;
    setIsLoading(true);
    startTimeRef.current = Date.now();
    retryCountRef.current = 0;
    isPollingActiveRef.current = true;

    pollTransaction();

    // Set maximum timeout
    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && isPollingActiveRef.current) {
        stopPolling();
        onError(
          new TransactionStatusUnknownError(
            "Transaction polling timed out after 5 minutes"
          )
        );
      }
    }, MAX_POLLING_TIME);
  };

  const pollTransaction = async () => {
    if (!isMountedRef.current || !isPollingActiveRef.current) return;

    try {
      // Normalize to the transaction-poll shape so callers don't need to
      // know which endpoint answered - the operation poll uses createdAt
      // where the transaction poll uses date.
      let response: PollTransactionStatusResponse;

      if (idTypeRef.current === "operation") {
        const operationResponse =
          await OperationsApiService.getOperationLongPolling(
            transactionIdRef.current
          );
        response = {
          ...operationResponse,
          data: {
            status: operationResponse.data?.status,
            date: operationResponse.data?.createdAt,
            transactionId: operationResponse.data?.transactionId,
            sessionId:
              operationResponse.data?.sessionId ||
              operationResponse.data?.outBankTransfer?.sessionId,
          },
        };
      } else {
        response = await TransactionsApiService.pollTransactionStatus(
          transactionIdRef.current
        );
      }

      if (!isMountedRef.current || !isPollingActiveRef.current) return;

      const status = response.data?.status;

      if (status === "SUCCESS" || status === "COMPLETED") {
        stopPolling();
        onSuccess(response);
        return;
      }

      if (status === "FAILED" || status === "DECLINED" || status === "ERROR" || status === "REVERSED") {
        stopPolling();
        onError(new Error(response.message || "Transaction failed"));
        return;
      }

      if (status === "PENDING" || status === "PROCESSING" || status === "INITIATED") {
        // Check if we've exceeded the maximum polling time
        if (Date.now() - startTimeRef.current >= MAX_POLLING_TIME) {
          stopPolling();
          onError(
            new TransactionStatusUnknownError(
              "Transaction polling timed out after 5 minutes"
            )
          );
          return;
        }
        pollingIntervalRef.current = setTimeout(pollTransaction, interval);
        return;
      }

      if (!status) {
        pollingIntervalRef.current = setTimeout(pollTransaction, interval);
        return;
      }

      // An unrecognised status is not a decline — the processor's real
      // outcome is still unknown, so surface it as unknown, not failed.
      stopPolling();
      onError(
        new TransactionStatusUnknownError(`Unknown transaction status: ${status}`)
      );

    } catch (error) {
      if (!isMountedRef.current || !isPollingActiveRef.current) return;

      // Handle timeout errors specifically
      if (error instanceof Error && error.message.includes("timeout")) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          pollingIntervalRef.current = setTimeout(pollTransaction, RETRY_DELAY);
          return;
        }
      }

      // A network/request error tells us nothing about the transaction's
      // real outcome — never surface it as a decline.
      stopPolling();
      onError(
        new TransactionStatusUnknownError(
          (error as Error)?.message || "Unable to confirm transaction status"
        )
      );
    }
  };

  const stopPolling = () => {
    clearTimeout(pollingIntervalRef.current);
    clearTimeout(timeoutRef.current);
    setIsLoading(false);
    isPollingActiveRef.current = false;
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  return { startPolling, isLoading, stopPolling };
};
